<template>
  <InputLabel :label="label" :tooltip="tooltip" :is-error="isError" :warning="warning">
    <VueDatepicker
      :format="dateFormat"
      :locale="locale"
      :format-locale="formatLocale"
      auto-apply
      close-on-auto-apply
      :enable-time-picker="false"
      v-bind="passthroughAttrs"
      :multi-calendars="multiCalendars"
      :input-class-name="input({ size: 'sm' })"
      :min-date="minDate"
      :max-date="maxDate"
    />
    <InputHint :hint="hint" :is-error="isError" />
  </InputLabel>
</template>

<script lang="ts" setup>
import { de, enUS } from 'date-fns/locale';
import { i18n } from '@/i18n';
import { computed, toValue, useAttrs } from 'vue';
import { useBreakpoints } from '@vueuse/core';
import VueDatepicker from '@vuepic/vue-datepicker';
import type { DpOptionEnabled } from '@vuepic/vue-datepicker';
import { getDateFormat } from '@/hooks/use-date-format';
import { cvBreakpoints } from '@/utils/breakpoints';
import InputLabel from './InputLabel.vue';
import InputHint from './InputHint.vue';
import { input } from '@carvia/theme/lib/cva/input';

defineOptions({ inheritAttrs: false });

const attrs = useAttrs();
const isWideLayout = useBreakpoints(cvBreakpoints).greaterOrEqual('md');

const passthroughAttrs = computed(() => {
  const { multiCalendars: _multiCalendars, ...rest } = attrs;
  return rest;
});

type MultiCalendars =
  | DpOptionEnabled
  | Partial<{ static: boolean; solo: boolean; count: number | string }>;

const multiCalendars = computed((): MultiCalendars | undefined => {
  if ('multiCalendars' in attrs) {
    return attrs.multiCalendars as MultiCalendars;
  }
  if ('range' in attrs && attrs.range !== false) {
    return isWideLayout.value;
  }
  return undefined;
});

const props = defineProps<{
  label?: string;
  hint?: string;
  isError?: boolean;
  warning?: string;
  tooltip?: string;
  dateFormat?: string;
  minDate?: Date | string;
  maxDate?: Date | string;
}>();

const locale = i18n.global.locale;
const formatLocale = computed(() => (toValue(locale) === 'de' ? de : enUS));

const dateFormat = computed(() => props.dateFormat ?? getDateFormat());
</script>
